import React from 'react'
import { createPortal } from 'react-dom'

import { CloseIcon } from '@ui/components/icons'
import clsx from 'clsx'

interface ModalProps {
  isOpen: boolean
  onClose: () => void
  children: React.ReactNode
  title?: string
  domNode?: HTMLElement
  modalSize?: 'small' | 'medium' | 'large' | 'full'
}

export const Modal = ({ isOpen, onClose, children, title, domNode, modalSize = 'medium' }: ModalProps) => {
  if (!isOpen) return null

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div
        id="modal-backdrop"
        className="fixed inset-0 cursor-pointer bg-black/50 transition-opacity"
        onClick={onClose}
      />

      {/* Modal Content */}
      <div
        className={clsx('relative z-50 w-full rounded-md bg-white shadow-xl', {
          'max-w-md': modalSize === 'small',
          'max-w-xl': modalSize === 'medium',
          'max-w-3xl': modalSize === 'large',
          'w-full': modalSize === 'full',
        })}>
        {/* Header */}
        {title && (
          <>
            <div className="flex items-center justify-between px-6 py-4">
              <h2 className="text-xl font-semibold">{title}</h2>
              <button
                data-testid="close-modal"
                onClick={onClose}
                className="ml-auto rounded-full p-2 transition-all duration-300 hover:bg-gray-100">
                <CloseIcon className="size-4 stroke-gray-900" />
              </button>
            </div>

            <div className="h-px w-full bg-gray-200" />
          </>
        )}

        {!title && (
          <button
            data-testid="close-modal"
            onClick={onClose}
            className="absolute right-2 top-4 rounded-full p-2 transition-all duration-300 hover:bg-gray-100">
            <CloseIcon className="size-4 stroke-gray-900" />
          </button>
        )}

        {children}
      </div>
    </div>,
    domNode ?? document.body
  )
}
